import React from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CtBtn from "./CustomTool/CtBtn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import invanotry from "../Images/invanotry_managment.png";
import gst from "../Images/gst.png";
import productimmg from "../Images/productimmg.png";
import stockimg from "../Images/stockimg.png";

//===================================================================
// 1. Hero Section
//===================================================================
const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Images in public folder
  const images = [invanotry, gst, productimmg, stockimg];

  return (
    <Box sx={{ bgcolor: "white", py: 8 }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left Column - Text */}
          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <Typography variant="subtitle1" color="primary" fontWeight="bold">
              Inventory Management Software
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 1,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Manage Inventory
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              with 1 Click!
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 3, fontSize: "1.1rem" }}
            >
              Your Complete Inventory solution for stock tracking, order
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 2, fontSize: "1.1rem" }}
            >
              management & stock movements. Control over low stock, overstock &
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 2, fontSize: "1.1rem" }}
            >
              expiring items. Take your business on the go!
            </Typography>

            <CtBtn
              variant="contained"
              onClick={() => window.open("https://accounts.ravitechworld.com/", "_blank")}
              sx={{
                bgcolor: "successful.main",
                px: 4,
                py: 1.5,
                mt: 4,
                fontWeight: "bold",
                "&:hover": { bgcolor: "successful.dark" },
              }}
              label="Book A Demo Of Inventory Software"
            />

            <Box
              display="flex"
              alignItems="center"
              mt={3}
              color="text.secondary"
            >
              <VerifiedUserIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body2">
                Trusted by Crores+ Businesses
              </Typography>
            </Box>
          </Grid>

          {/* Right Column - 4 Images */}
          <Grid
            size={{ xs: 12, md: 5 }}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)", // 2x2 grid
              gridAutoRows: "150px", // fixed row height
              gap: 2,
            }}
          >
            {images.map((img, i) => (
              <Box
                key={i}
                component="img"
                src={img} // public folder paths
                alt={`Screenshot ${i + 1}`}
                sx={{
                  borderRadius: 2,
                  boxShadow: 4,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover", // maintain aspect ratio and fill
                }}
              />
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

//===================================================================
// 2. Feature Card Component
//===================================================================
const FeatureCard = ({ title, description, points }) => (
  <Paper
    elevation={3}
    sx={{
      p: { xs: 2, sm: 3, md: 4 },
      borderRadius: 3,
      height: "auto",
      maxWidth: 360,
      width: "100%",
      transition: "0.3s",
      "&:hover": { boxShadow: 6 },
    }}
  >
    <Typography variant="h5" fontWeight="bold" color="text.primary">
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      {description}
    </Typography>
    <Box component="ul" sx={{ mt: 3, pl: 0, listStyle: "none" }}>
      {points.map((point, i) => (
        <Box
          key={i}
          component="li"
          display="flex"
          alignItems="flex-start"
          mb={1.5}
        >
          <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.primary">
            {point}
          </Typography>
        </Box>
      ))}
    </Box>
  </Paper>
);

//===================================================================
// 3. Features Section
//===================================================================
const features = [
  {
    title: "Goods/Store Management",
    description:
      "Manage multiple goods and store data in one place with advanced inventory tracking.",
    points: [
      "Stock inward & outward tracking",
      "Batch, barcode, serial number & expiry date management",
      "Daily stock reports & analytics",
      "Real-time inventory sync across devices",
      "GST compliance ready",
    ],
  },
  {
    title: "Streamlined Stock Management",
    description:
      "Efficiently track, categorize, and analyze your stock with real-time updates.",
    points: [
      "Barcode integration & scanning",
      "Category & brand wise stock tracking",
      "Low stock alerts & auto reorder",
      "Stock adjustment & damaged stock reports",
      "Visual & summary stock reports",
    ],
  },
  {
    title: "Effortless Order Management",
    description:
      "Simplify order processes with automated tracking, packaging, and payments.",
    points: [
      "Packaging & shipment tracking",
      "Order invoices (regular & e-invoices)",
      "Payment reminders & tracking",
      "Outstanding collection reports",
      "Direct-to-customer stock allocation",
    ],
  },
  {
    title: "Stay GST Compliant with Ease",
    description:
      "Easily generate GST invoices & file returns directly within the system.",
    points: [
      "GST-compliant invoices & bills",
      "Expense & input credit tracking",
      "Error-free e-invoicing",
      "Easy GST filing integration",
      "Simplified audit-ready reports",
    ],
  },
  {
    title: "Comprehensive Business Reports",
    description:
      "Gain deep insights into sales, stock, and profits with detailed reports.",
    points: [
      "Inventory & sales analysis",
      "Profit & loss statements",
      "Customizable report templates",
      "Supplier & purchase reports",
      "Stock valuation reports",
    ],
  },
  {
    title: "Seamless Hardware & WhatsApp Integration",
    description:
      "Connect with customers & devices seamlessly for smooth business operations.",
    points: [
      "WhatsApp billing & reminders",
      "Automated follow-ups",
      "Receipt sharing & updates",
      "Thermal printer & barcode scanner support",
      "QR code payment options",
    ],
  },
];

const FeaturesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ bgcolor: "grey.50", py: { xs: 6, sm: 8, md: 10 } }}>
      <Container maxWidth="lg">
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          textAlign="center"
          color="text.primary"
          sx={{ px: { xs: 2, sm: 0 } }}
        >
          How Inventory Management Made{" "}
          <Box component="span" color="primary.main">
            Easy, Efficient, Effective
          </Box>{" "}
          by RaviTechWorld
        </Typography>

        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 4 }}
          justifyContent="center"
          sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
        >
          {features.map((f, i) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 6 }}
              key={i}
              display="flex"
              justifyContent="center"
            >
              <FeatureCard {...f} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

//===================================================================
// Main Page
//===================================================================
export default function InventoryPage() {
  return (
    <Box>
      <HeroSection />
      <FeaturesSection />
    </Box>
  );
}
